/**
 * R78 · Master Presets — save/apply named groups of package lines.
 * ----------------------------------------------------------------
 * Two modes accessible from a single dialog:
 *   • Save current lines as a preset (name + optional description).
 *   • Apply an existing preset — appends its lines to the current package
 *     with fresh IDs, and merges its sections (preserving existing ones).
 * Delete is also available inline for each preset.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FileStack, Trash2, Loader2, Save, Play, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { FREQ_LABELS } from "@/pages/app/Packages";

export default function ActivityPresetsDialog({
  open, onOpenChange, wsId,
  currentLines, currentSections,
  onApply,
  canManage,
  testid = "pkg-presets-dialog",
}) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");   // R80
  const [filter, setFilter] = useState("_all");   // R80 · category filter
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (id) => setExpanded((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/activity-presets`);
      setPresets(r.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load presets")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open && wsId) load(); /* eslint-disable-next-line */ }, [open, wsId]);

  const save = async () => {
    if (!name.trim()) { toast.error("Give the preset a name"); return; }
    if (!(currentLines || []).length) { toast.error("Add at least one line before saving"); return; }
    setBusy(true);
    try {
      // Strip transient enrichment fields (monthly_qty etc.) and normalise skills.
      const cleanLines = (currentLines || []).map((l) => ({
        activity_type_id: l.activity_type_id || null,
        name: l.name, category: l.category || "other",
        is_external: l.is_external !== false,
        frequency: l.frequency || "monthly",
        count: Number(l.count) || 1,
        effort_hours: Number(l.effort_hours) || 0,
        skill_breakdown: (l.skill_breakdown || []).filter((s) => s.designation)
          .map((s) => ({ designation: s.designation, hours: Number(s.hours) || 0 })),
        rate: Number(l.rate) || 0,
        notes: l.notes || null,
        section_id: l.section_id || null,
        sort_order: l.sort_order ?? null,
      }));
      const cleanSections = (currentSections || []).map((s) => ({
        id: s.id, name: s.name, order: s.order || 0, color: s.color || null,
      }));
      await api.post(`/workspaces/${wsId}/activity-presets`, {
        name: name.trim(), description: description.trim() || null,
        category: (category || "").trim() || null,
        lines: cleanLines, sections: cleanSections,
      });
      toast.success(`Preset "${name}" saved`);
      setName(""); setDescription(""); setCategory("");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save preset failed")); }
    finally { setBusy(false); }
  };

  const apply = (p) => {
    // Merge: current + preset.lines (with fresh section_id remapping if
    // needed). We regenerate ids client-side to avoid dup-key collisions.
    const now = Date.now();
    const sectionIdMap = new Map();
    const existingSectionIds = new Set((currentSections || []).map((s) => s.id));
    const mergedSections = [...(currentSections || [])];
    (p.sections || []).forEach((s) => {
      // If an existing section with the same NAME already exists, reuse its id.
      const existing = mergedSections.find((es) => es.name === s.name);
      if (existing) {
        sectionIdMap.set(s.id, existing.id);
      } else {
        const newId = `s_${now}_${Math.random().toString(36).slice(2, 8)}`;
        sectionIdMap.set(s.id, newId);
        mergedSections.push({ id: newId, name: s.name, order: (mergedSections.length + 1) * 10, color: s.color || null });
      }
    });
    // Deduplicate section ids just in case.
    existingSectionIds.forEach((id) => id);
    const currentCount = (currentLines || []).length;
    const newLines = (p.lines || []).map((l, i) => ({
      ...l,
      id: `l_${now}_${i}_${Math.random().toString(36).slice(2, 6)}`,
      section_id: l.section_id ? (sectionIdMap.get(l.section_id) || null) : null,
      sort_order: (currentCount + i + 1) * 10,
    }));
    onApply([...(currentLines || []), ...newLines], mergedSections);
    onOpenChange(false);
    toast.success(`Added ${newLines.length} line${newLines.length === 1 ? "" : "s"} from "${p.name}"`);
  };

  const del = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await api.delete(`/activity-presets/${p.id}`);
      toast.success(`Deleted "${p.name}"`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileStack className="w-4 h-4" /> Master presets</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {canManage && (
            <div className="border border-border p-3 space-y-2 bg-muted/20">
              <Label className="label-eyebrow">Save current lines as a preset</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Preset name (e.g. Social starter)"
                       className="h-9 rounded-none" data-testid={`${testid}-name`} />
                <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category (e.g. Full-service, Monthly retainer, One-off)"
                       className="h-9 rounded-none" data-testid={`${testid}-category`} list={`${testid}-cat-suggest`} />
                <datalist id={`${testid}-cat-suggest`}>
                  <option value="Full-service" />
                  <option value="Monthly retainer" />
                  <option value="One-off" />
                  <option value="Sprint" />
                  <option value="Campaign" />
                </datalist>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Will save <strong>{(currentLines || []).length}</strong> line{((currentLines || []).length) === 1 ? "" : "s"} and <strong>{(currentSections || []).length}</strong> section{((currentSections || []).length) === 1 ? "" : "s"}.
              </div>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                        placeholder="Short description (optional)"
                        className="h-16 rounded-none text-xs" data-testid={`${testid}-desc`} />
              <div className="flex justify-end">
                <Button size="sm" className="rounded-none" onClick={save} disabled={busy} data-testid={`${testid}-save`}>
                  {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save preset
                </Button>
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Label className="label-eyebrow">Apply an existing preset</Label>
              {/* R80 · Category filter chips */}
              {(() => {
                const cats = Array.from(new Set(presets.map((p) => p.category).filter(Boolean))).sort();
                if (!cats.length) return null;
                return (
                  <div className="ml-auto flex gap-1 flex-wrap" data-testid={`${testid}-filter`}>
                    {["_all", ...cats].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setFilter(c)}
                        className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${filter === c ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-muted/40"}`}
                        data-testid={`${testid}-filter-${c}`}
                      >
                        {c === "_all" ? `All (${presets.length})` : `${c} (${presets.filter((p) => p.category === c).length})`}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
            {loading ? (
              <div className="text-xs text-muted-foreground py-3 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </div>
            ) : presets.length === 0 ? (
              <div className="text-xs italic text-muted-foreground py-3">No presets saved yet.</div>
            ) : (
              <div className="border border-border divide-y divide-border">
                {presets
                  .filter((p) => filter === "_all" || p.category === filter)
                  .map((p) => {
                  const isOpen = expanded.has(p.id);
                  // Group lines by section for the mini preview, mirroring the
                  // grouping shown in the actual package preview panel.
                  const sectionMap = new Map();
                  (p.sections || []).forEach((s) => sectionMap.set(s.id, { name: s.name, order: s.order, items: [] }));
                  const ungrouped = [];
                  (p.lines || []).forEach((l) => {
                    if (l.section_id && sectionMap.has(l.section_id)) {
                      sectionMap.get(l.section_id).items.push(l);
                    } else {
                      ungrouped.push(l);
                    }
                  });
                  const grouped = Array.from(sectionMap.values())
                    .filter((g) => g.items.length > 0)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                  return (
                    <div key={p.id} data-testid={`${testid}-row-${p.id}`}>
                      <div className="flex items-center gap-2 px-3 py-2 hover:bg-muted/20">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(p.id)}
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`${testid}-expand-${p.id}`}
                          title={isOpen ? "Hide items" : "Show items"}
                        >
                          {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium truncate">{p.name}</div>
                            {p.category && (
                              <span className="text-[9px] uppercase tracking-widest bg-primary/10 text-primary border border-primary/30 px-1.5 py-0.5" data-testid={`${testid}-cat-${p.id}`}>
                                {p.category}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {(p.lines || []).length} line{((p.lines || []).length === 1 ? "" : "s")}
                            {(p.sections || []).length ? ` · ${(p.sections || []).length} section${(p.sections || []).length === 1 ? "" : "s"}` : ""}
                            {p.description ? ` · ${p.description}` : ""}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => apply(p)} data-testid={`${testid}-apply-${p.id}`}>
                          <Play className="w-3 h-3 mr-1" /> Apply
                        </Button>
                        {canManage && (
                          <button type="button" onClick={() => del(p)} className="text-destructive hover:opacity-80" data-testid={`${testid}-del-${p.id}`} title="Delete preset">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      {isOpen && (
                        <div className="px-3 pb-3 pt-1 bg-muted/10" data-testid={`${testid}-items-${p.id}`}>
                          {grouped.map((g, gi) => (
                            <div key={gi} className="mb-2">
                              <div className="text-[10px] uppercase tracking-widest text-primary font-bold mb-0.5">{g.name}</div>
                              <MiniItemsTable items={g.items} />
                            </div>
                          ))}
                          {ungrouped.length > 0 && (
                            <div className="mb-1">
                              {grouped.length > 0 && (
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-0.5">Other</div>
                              )}
                              <MiniItemsTable items={ungrouped} />
                            </div>
                          )}
                          {(p.lines || []).length === 0 && (
                            <div className="text-[11px] italic text-muted-foreground">Empty preset.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)} data-testid={`${testid}-close`}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MiniItemsTable({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <table className="w-full text-[11px] border border-border border-collapse">
      <thead>
        <tr className="bg-muted/30 text-[9px] uppercase tracking-widest text-muted-foreground">
          <th className="text-left px-2 py-0.5 border-r border-border">Item</th>
          <th className="text-left px-2 py-0.5 border-r border-border w-16">Category</th>
          <th className="text-right px-2 py-0.5 border-r border-border w-12">Count</th>
          <th className="text-left px-2 py-0.5 border-r border-border w-20">Frequency</th>
          <th className="text-right px-2 py-0.5 w-12">Effort/u</th>
        </tr>
      </thead>
      <tbody>
        {items.map((l, i) => (
          <tr key={i} className="border-t border-border">
            <td className="border-r border-border px-2 py-0.5 truncate max-w-[220px]" title={l.name}>{l.name}</td>
            <td className="border-r border-border px-2 py-0.5">{l.category || "other"}</td>
            <td className="border-r border-border px-2 py-0.5 text-right font-mono">{Number(l.count) || 0}</td>
            <td className="border-r border-border px-2 py-0.5">{FREQ_LABELS[l.frequency] || l.frequency || "—"}</td>
            <td className="px-2 py-0.5 text-right font-mono">{Number(l.effort_hours) || 0}h</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
